<template>
  <div>
    <Movementmenu />

    <v-container fluid class="pa-6" tag="section">
      <!-- ─── Header ─── -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between mb-2">
            <div>
              <h2 class="text-h4 font-weight-bold primary--text">
                <v-icon large color="primary" class="mr-2"
                  >mdi-account-arrow-left-outline</v-icon
                >
                ประมวลผลข้อมูลการย้าย (แบบปกติ)
              </h2>
              <div class="subtitle-1 grey--text text--darken-1 mt-1">
                ระบบวิเคราะห์และจับคู่ตำแหน่งว่างสำหรับการย้าย รอบ
                {{ periods.period_times }}/{{ periods.period_year }}
              </div>
            </div>
            <div>
              <v-btn
                text
                rounded
                color="primary"
                class="mr-2"
                @click="guideDialog = true"
              >
                <v-icon left>mdi-help-circle-outline</v-icon> วิธีใช้งานหน้านี้
              </v-btn>
              <v-btn
                color="indigo"
                dark
                rounded
                large
                elevation="2"
                @click="conditions_transferQueryAll"
                :loading="loading"
              >
                <v-icon left>mdi-refresh</v-icon> โหลดข้อมูลล่าสุด
              </v-btn>
            </div>
          </div>
        </v-col>

        <!-- ─── Stats Cards ─── -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #1976D2"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">รายการทั้งหมด</div>
                <div class="text-h4 font-weight-bold">{{ stats.total }}</div>
              </div>
              <v-avatar color="blue lighten-5" size="56">
                <v-icon color="blue" x-large>mdi-format-list-bulleted</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #2E7D32"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">
                  จับคู่สำเร็จ (Matched)
                </div>
                <div class="text-h4 font-weight-bold" style="color: #2E7D32">
                  {{ stats.matched }}
                </div>
              </div>
              <v-avatar color="green lighten-5" size="56">
                <v-icon color="green darken-3" x-large
                  >mdi-check-decagram</v-icon
                >
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #E65100"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">อัตราว่างรับย้าย</div>
                <div class="text-h4 font-weight-bold" style="color: #E65100">
                  {{ stats.vacancies }}
                </div>
              </div>
              <v-avatar color="orange lighten-5" size="56">
                <v-icon color="orange darken-4" x-large>mdi-door-open</v-icon>
              </v-avatar>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="rounded-lg pa-4 stat-card"
            elevation="2"
            style="border-left: 5px solid #6A1B9A"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <div class="text-overline grey--text">รอดำเนินการ</div>
                <div class="text-h4 font-weight-bold" style="color: #6A1B9A">
                  {{ stats.pending }}
                </div>
                <div v-if="stats.pendingNoVacancy > 0" class="caption red--text text--darken-2">
                  ในจำนวนนี้ {{ stats.pendingNoVacancy }} ราย ปลายทางไม่มีตำแหน่งว่างแล้ว
                </div>
              </div>
              <v-avatar color="purple lighten-5" size="56">
                <v-icon color="purple darken-3" x-large
                  >mdi-progress-clock</v-icon
                >
              </v-avatar>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ─── Legend Bar ─── -->
      <v-card elevation="1" class="rounded-lg mb-4 pa-3">
        <div class="d-flex align-center flex-wrap" style="gap: 16px">
          <span class="text-subtitle-2 grey--text text--darken-2 mr-2">
            <v-icon small class="mr-1">mdi-palette</v-icon> สัญลักษณ์สี:
          </span>
          <div class="d-flex align-center">
            <span
              class="legend-dot"
              style="background: #E8F5E9; border: 2px solid #2E7D32"
            ></span>
            <span class="caption font-weight-medium"
              >พิจารณาแล้ว / จับคู่สำเร็จ</span
            >
          </div>
          <div class="d-flex align-center">
            <span
              class="legend-dot"
              style="background: #FFF3E0; border: 2px solid #E65100"
            ></span>
            <span class="caption font-weight-medium"
              >รอดำเนินการ (มีอัตราว่าง)</span
            >
          </div>
          <div class="d-flex align-center">
            <span
              class="legend-dot"
              style="background: #FFEBEE; border: 2px solid #C62828"
            ></span>
            <span class="caption font-weight-medium"
              >ไม่มีตำแหน่งว่าง / ไม่สามารถพิจารณาได้</span
            >
          </div>
          <div class="d-flex align-center">
            <span
              class="legend-dot"
              style="background: #E3F2FD; border: 2px solid #1565C0"
            ></span>
            <span class="caption font-weight-medium"
              >สับเปลี่ยน / แลกเปลี่ยน</span
            >
          </div>
        </div>
      </v-card>

      <!-- ─── Toolbar ─── -->
      <v-card elevation="2" class="rounded-lg mb-4">
        <v-toolbar flat color="white" class="pt-2">
          <v-tabs
            v-model="activeTab"
            background-color="transparent"
            color="primary"
          >
            <v-tab @click="conditions_transferQueryAll">
              <v-icon left>mdi-view-list</v-icon> แสดงทั้งหมด
            </v-tab>
            <v-tab @click="OnetoOne">
              <v-icon left>mdi-filter-variant</v-icon> กรอง 1:1 (One to One)
            </v-tab>
          </v-tabs>
          <v-spacer></v-spacer>

          <!-- ปิดโดย default เสมอ — เดิมระบบกรองรายการที่ไม่มีตำแหน่งว่างออกให้อัตโนมัติ ทำให้
               ผู้ยื่นย้ายหายไปจากตารางเงียบๆ โดยไม่มีคำอธิบาย ตอนนี้แสดงทุกรายการเป็นค่าเริ่มต้น
               เสมอ ผู้ใช้ต้องเปิดสวิตช์นี้เองถ้าต้องการซ่อนรายการที่ไม่มีตำแหน่งว่างแล้ว -->
          <v-switch
            v-model="onlyWithVacancy"
            label="แสดงเฉพาะที่มีตำแหน่งว่าง"
            color="success"
            dense
            hide-details
            class="mt-2 mr-4"
            @change="conditions_transferQueryAll"
          ></v-switch>

          <div class="d-flex align-center mt-2 mr-2" style="max-width: 300px">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="ค้นหาข้อมูล..."
              dense
              outlined
              rounded
              hide-details
            ></v-text-field>
          </div>

          <v-btn
            color="success"
            outlined
            rounded
            class="mt-2"
            @click="exportToExcel"
          >
            <v-icon left>mdi-file-excel</v-icon> Export Excel
          </v-btn>
        </v-toolbar>

        <v-alert
          v-if="activeTab === 1"
          type="info"
          text
          dense
          class="mx-4 mb-4"
        >
          <strong>1:1 (One to One)</strong> คือกรณี "ไม่มีคู่แข่ง" — สถานศึกษาเปิดรับสาขานี้ มีตำแหน่งว่าง
          และมีผู้ยื่นขอย้ายเข้ามาที่สถานศึกษา+สาขานี้เพียงคนเดียวเท่านั้น เหมาะสำหรับพิจารณาอนุมัติได้ทันที
          โดยไม่ต้องจัดลำดับเปรียบเทียบผู้สมัคร (ถ้าไม่พบรายการ แปลว่าไม่มีกรณีที่ตรงเงื่อนไขนี้ในรอบที่เลือก)
        </v-alert>

        <v-expansion-panels flat accordion>
          <v-expansion-panel>
            <v-expansion-panel-header class="grey lighten-5 text-subtitle-2">
              <span class="grey--text text--darken-2">
                <v-icon small class="mr-2">mdi-cog</v-icon> ตั้งค่าคอลัมน์ตาราง
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-4">
              <v-select
                v-model="selectedHeaders"
                :items="headersMap"
                label="เลือกคอลัมน์ที่ต้องการแสดง"
                multiple
                outlined
                dense
                chips
                small-chips
                deletable-chips
                return-object
              ></v-select>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>

      <!-- ─── Data Table ─── -->
      <v-card elevation="2" class="rounded-lg process-table-card">
        <v-data-table
          :headers="showHeaders"
          :items="conditions_transfers"
          :search="search"
          :loading="loading"
          group-by="college_code"
          :items-per-page="-1"
          :item-class="row_classes"
          class="elevation-0 process-table"
        >
          <!-- หัวกลุ่ม: สถานศึกษาปลายทาง 1 กลุ่ม = 1 สถานศึกษา รวมผู้ยื่นย้ายที่ขอเข้ามาทั้งหมด
               แสดงอัตรากำลังปลายทางไว้ที่หัวกลุ่มครั้งเดียว (ค่าเดียวกันทุกแถวในกลุ่ม) -->
          <template v-slot:group.header="{ items, isOpen, toggle }">
            <td :colspan="showHeaders.length" class="group-header-row">
              <div class="d-flex align-center" style="gap: 12px;">
                <v-btn icon small @click="toggle">
                  <v-icon>{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </v-btn>
                <v-icon color="primary" small>mdi-school</v-icon>
                <span class="font-weight-bold text-subtitle-2">
                  {{ items[0].college_name }}
                </span>
                <v-chip x-small outlined color="grey darken-1">
                  รหัส {{ items[0].college_code }}
                </v-chip>
                <v-chip x-small color="blue-grey lighten-4">
                  ผู้ยื่นย้ายเข้า {{ items.length }} ราย
                </v-chip>
                <!-- สรุปอัตราตำแหน่งของสถานศึกษาปลายทางนี้ (เหมือน admin/man_power) — แก้ปัญหาที่
                     เจ้าหน้าที่เพิ่งเริ่มงานไม่เข้าใจว่าทำไมผู้ยื่นย้ายบางคนไม่ถูกประมวลผล เพราะเดิม
                     ถ้าตำแหน่งว่างถูกจองครบแล้ว แถวทั้งหมดจะหายไปจากตารางเงียบๆ โดยไม่มีคำอธิบาย -->
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      dark
                      :color="items[0].num_position > 0 ? '#2E7D32' : '#C62828'"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small left>mdi-door</v-icon>
                      อัตราทั้งหมด {{ items[0].num_position_total }} · จองแล้ว
                      {{ items[0].num_position_booked }} · ว่างจริง {{ items[0].num_position }}
                    </v-chip>
                  </template>
                  <span>
                    สถานศึกษานี้มีอัตราว่างรับย้ายทั้งหมด {{ items[0].num_position_total }} อัตรา
                    ถูกจองไปแล้ว {{ items[0].num_position_booked }} อัตรา
                    เหลือว่างจริง {{ items[0].num_position }} อัตรา
                    <span v-if="items[0].num_position == 0">
                      — ผู้ยื่นย้ายเข้ากลุ่มนี้ยังคงแสดงในตารางไว้ให้พิจารณา แต่ยังประมวลผลลงตำแหน่งไม่ได้
                      จนกว่าจะมีตำแหน่งว่างเพิ่ม
                    </span>
                  </span>
                </v-tooltip>
                <v-tooltip top v-if="items[0].dest_rate_diff !== null && items[0].dest_rate_diff !== undefined">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      dark
                      :color="rateDiffColor(items[0].dest_rate_diff)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon x-small left>mdi-account-group</v-icon>
                      อัตรากำลังปลายทาง
                      {{ items[0].dest_rate_diff > 0 ? "+" : ""
                      }}{{ items[0].dest_rate_diff }}
                    </v-chip>
                  </template>
                  <span>
                    {{ items[0].dest_rate_status || "-" }}
                    {{ items[0].dest_rate_diff > 0 ? "+" : ""
                    }}{{ items[0].dest_rate_diff }} คน
                  </span>
                </v-tooltip>
              </div>
            </td>
          </template>

          <!-- สถานะการย้าย Column -->
          <template v-slot:item.college_code_susss="{ item }">
            <div
              v-if="item.college_code_susss >= 1"
              class="d-flex flex-column align-center justify-center"
            >
              <v-chip
                color="#2E7D32"
                dark
                small
                class="font-weight-bold status-chip-success"
              >
                <v-icon left small>mdi-check-circle</v-icon>
                สำเร็จ
              </v-chip>
              <div
                v-if="item.college_name_suss"
                class="caption green--text text--darken-3 mt-1 text-center"
                style="max-width: 140px; line-height: 1.3;"
              >
                <v-icon x-small color="green darken-3"
                  >mdi-map-marker-check</v-icon
                >
                ย้ายไป: {{ item.college_name_suss }}
              </div>
            </div>
            <div v-else class="d-flex align-center justify-center">
              <v-chip
                v-if="item.num_position === '0' || item.num_position == 0"
                color="#C62828"
                dark
                small
                class="font-weight-bold status-chip-no-position"
              >
                <v-icon left small>mdi-close-circle</v-icon>
                ไม่มีตำแหน่ง
              </v-chip>
              <v-chip
                v-else-if="
                  item.status_select === 'sw_normal' ||
                    item.status_select === 'sw_agree'
                "
                color="#1565C0"
                dark
                small
                class="font-weight-bold"
              >
                <v-icon left small>mdi-swap-horizontal</v-icon>
                สับเปลี่ยน
              </v-chip>
              <v-chip
                v-else
                color="#E65100"
                dark
                small
                class="font-weight-bold status-chip-pending"
              >
                <v-icon left small>mdi-clock-outline</v-icon>
                รอพิจารณา
              </v-chip>
            </div>
          </template>

          <!-- สถานศึกษา Column -->
          <template v-slot:item.college_name="{ item }">
            <v-chip
              color="blue lighten-5"
              text-color="blue darken-3"
              small
              label
            >
              <v-icon left x-small>mdi-bank</v-icon> {{ item.college_name }}
            </v-chip>
          </template>

          <!-- รอบ/ปี Column -->
          <template v-slot:item.time_s="{ item }">
            <span class="font-weight-bold grey--text text--darken-2"
              >{{ item.time_s }}/{{ item.year_s }}</span
            >
          </template>

          <!-- อัตราว่าง Column -->
          <template v-slot:item.num_position="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  :color="
                    item.num_position === '0' || item.num_position == 0
                      ? '#FFCDD2'
                      : '#C8E6C9'
                  "
                  :text-color="
                    item.num_position === '0' || item.num_position == 0
                      ? '#B71C1C'
                      : '#1B5E20'
                  "
                  small
                  label
                  class="font-weight-bold"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="collegePositon(item.college_code)"
                  style="cursor: pointer"
                >
                  <v-icon left x-small>{{
                    item.num_position === "0" || item.num_position == 0
                      ? "mdi-close-circle"
                      : "mdi-check-circle"
                  }}</v-icon>
                  {{ item.num_position }} อัตรา
                </v-chip>
              </template>
              <span>คลิกเพื่อดูรายละเอียดอัตราว่าง</span>
            </v-tooltip>
          </template>

          <!-- ผู้ขอ Column — เน้นเปรียบเทียบกับจำนวนรับ -->
          <template v-slot:item.personnel_num_s="{ item }">
            <v-chip
              :color="getApplicantChipColor(item)"
              :text-color="getApplicantChipTextColor(item)"
              small
              label
              class="font-weight-bold"
            >
              {{ item.personnel_num_s }}
              <span v-if="item.personnel_num_s > item.quantity_n" class="ml-1">
                <v-icon x-small :color="getApplicantChipTextColor(item)"
                  >mdi-account-group</v-icon
                >
              </span>
            </v-chip>
          </template>

          <!-- คะแนน Column: จำนวนเหตุผลการย้ายที่ระบุ (reason_1..4 รวมได้ 0-4 ข้อ) -->
          <template v-slot:item.point_s="{ item }">
            <span
              v-if="item.point_s"
              class="font-weight-bold"
              :class="getScoreClass(item.point_s)"
            >
              {{ item.point_s }}
            </span>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- คะแนนกรรมการ Column: คะแนนกรรมการ สอจ. (tpvecprovince_score) + คะแนนส่วนกลาง (score_1420) -->
          <template v-slot:item.committee_score="{ item }">
            <v-tooltip top v-if="item.committee_score">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="font-weight-bold"
                  :class="getCommitteeScoreClass(item.committee_score)"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ formatScore(item.committee_score) }}
                </span>
              </template>
              <span>
                คะแนน สอจ. {{ formatScore(item.tpvecprovince_score) }} +
                คะแนนส่วนกลาง {{ formatScore(item.score_1420) }} =
                {{ formatScore(item.committee_score) }}
              </span>
            </v-tooltip>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- อัตรากำลัง (ปลายทาง) Column -->
          <template v-slot:item.dest_rate_diff="{ item }">
            <v-tooltip top v-if="item.dest_rate_diff !== null && item.dest_rate_diff !== undefined">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  :color="rateDiffColor(item.dest_rate_diff)"
                  dark
                  x-small
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.dest_rate_diff > 0 ? "+" : "" }}{{ item.dest_rate_diff }}
                </v-chip>
              </template>
              <span>
                อัตรากำลังสถานศึกษาปลายทาง ({{ item.college_name }}):
                {{ item.dest_rate_status || "-" }}
                {{ item.dest_rate_diff > 0 ? "+" : ""
                }}{{ item.dest_rate_diff }} คน — ใช้จัดลำดับ priority
                ตำแหน่งที่ขาดเกณฑ์มากกว่าจะแสดงขึ้นก่อน
              </span>
            </v-tooltip>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- อัตรากำลัง (ต้นทาง) Column -->
          <template v-slot:item.origin_rate_diff="{ item }">
            <v-tooltip top v-if="item.origin_rate_diff !== null && item.origin_rate_diff !== undefined">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  :color="rateDiffColor(item.origin_rate_diff)"
                  dark
                  x-small
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.origin_rate_diff > 0 ? "+" : "" }}{{ item.origin_rate_diff }}
                </v-chip>
              </template>
              <span>
                อัตรากำลังสถานศึกษาต้นทาง ({{ item.college_name_now }}):
                {{ item.origin_rate_status || "-" }}
                {{ item.origin_rate_diff > 0 ? "+" : ""
                }}{{ item.origin_rate_diff }} คน — ใช้ประกอบการพิจารณาคู่กับ
                อัตรากำลังปลายทาง
              </span>
            </v-tooltip>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- สรุปผล (คำอธิบาย) Column — สรุปข้อมูลที่คำนวณแล้วให้เป็นประโยคภาษาไทยอ่านง่าย
               (ไม่ใช้ AI/API ภายนอกใดๆ — ประกอบข้อความจากฟิลด์ที่คำนวณอยู่แล้วในฝั่ง backend
               เท่านั้น จึงไม่มีความเสี่ยงเรื่อง PDPA และไม่กระทบตรรกะการจัดลำดับ/คำนวณเดิมเลย) -->
          <template v-slot:item.explain_result="{ item }">
            <span class="caption" style="line-height: 1.5;">{{ explainResult(item) }}</span>
          </template>

          <!-- ความเห็น ผอ. Column -->
          <template v-slot:item.comment_dr_c="{ item }">
            <div class="d-flex flex-wrap" style="gap: 4px">
              <v-chip
                v-if="item.comment_dr_c === 'approp'"
                x-small
                color="#2E7D32"
                dark
                class="font-weight-medium"
                >เห็นควร</v-chip
              >
              <v-chip
                v-else-if="item.comment_dr_c === 'inapprop'"
                x-small
                color="#E65100"
                dark
                class="font-weight-medium"
                >ไม่เห็นควร</v-chip
              >
              <v-chip
                v-else-if="item.comment_dr_c === 'suspend'"
                x-small
                color="#C62828"
                dark
                class="font-weight-medium"
                >ระงับย้าย</v-chip
              >
              <v-chip
                v-else
                x-small
                color="grey lighten-2"
                class="font-weight-medium"
                >รอความเห็น</v-chip
              >

              <v-chip
                v-if="item.status_document === 'complete'"
                x-small
                outlined
                color="#2E7D32"
                class="font-weight-medium"
                >เอกสารครบ</v-chip
              >
              <v-chip
                v-else-if="item.status_document === 'incomplete'"
                x-small
                outlined
                color="#E65100"
                class="font-weight-medium"
                >เอกสารไม่ครบ</v-chip
              >
            </div>
          </template>

          <!-- สถานะเงื่อนไข Column -->
          <template v-slot:item.status_process="{ item }">
            <v-icon
              v-if="item.personnel_num_s <= item.quantity_n"
              color="#2E7D32"
            >
              mdi-check-decagram
            </v-icon>
            <v-icon v-else color="#E65100"> mdi-alert-circle-outline </v-icon>
          </template>

          <!-- จัดการ Column -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-center">
              <v-chip
                v-if="item.status_select === 'sw_normal'"
                x-small
                color="#E3F2FD"
                text-color="#1565C0"
                class="mr-1 font-weight-bold"
                >สับเปลี่ยน(3)</v-chip
              >
              <v-chip
                v-else-if="item.status_select === 'sw_agree'"
                x-small
                color="#FCE4EC"
                text-color="#AD1457"
                class="mr-1 font-weight-bold"
                >แลกเปลี่ยน(4)</v-chip
              >

              <v-tooltip bottom v-else-if="item.college_code_susss >= 1">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="#C62828"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="deletePosition(item.id_ref)"
                  >
                    <v-icon small>mdi-delete-circle</v-icon>
                  </v-btn>
                </template>
                <span>ยกเลิกรายการ</span>
              </v-tooltip>

              <v-tooltip bottom v-else>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    :color="getActionColor(item)"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="
                      select_idPosition(
                        item.id_tfl,
                        item.id_tfp,
                        item.college_code
                      )
                    "
                  >
                    <v-icon>mdi-file-document-edit</v-icon>
                  </v-btn>
                </template>
                <span>บันทึก/เลือกตำแหน่ง</span>
              </v-tooltip>
            </div>
          </template>

          <!-- ชื่อผู้ย้าย Column -->
          <template v-slot:item.personnel_name="{ item }">
            <span class="font-weight-medium">{{ item.personnel_name }}</span>
          </template>

          <!-- สถานศึกษาเดิม Column -->
          <template v-slot:item.college_name_now="{ item }">
            <span class="caption grey--text text--darken-1">{{
              item.college_name_now
            }}</span>
          </template>
        </v-data-table>
      </v-card>

      <!-- ─── Cancel Dialog ─── -->
      <v-dialog v-model="canceldialog" persistent max-width="600px">
        <v-card class="rounded-lg">
          <v-toolbar color="#C62828" dark flat dense>
            <v-icon class="mr-2">mdi-alert</v-icon> ยืนยันการยกเลิกรายการ
          </v-toolbar>
          <v-card-text class="pt-4">
            <div class="text-body-1">
              คุณต้องการยกเลิกการบันทึกตำแหน่งของบุคลากรรายนี้ใช่หรือไม่?
            </div>
            <v-list dense class="grey lighten-5 mt-2 rounded">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold"
                    >{{ transference_personnels_id_ref.title_s
                    }}{{ transference_personnels_id_ref.frist_name }}
                    {{
                      transference_personnels_id_ref.last_name
                    }}</v-list-item-title
                  >
                  <v-list-item-subtitle
                    >จาก:
                    {{
                      transference_personnels_id_ref.college_name
                    }}</v-list-item-subtitle
                  >
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="canceldialog = false">ปิดหน้าต่าง</v-btn>
            <v-btn color="#C62828" dark elevation="0" @click="cancelSubmit"
              >ยืนยันการลบ</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ─── College Position Dialog ─── -->
      <v-dialog v-model="college_postionDialog" max-width="900px">
        <v-card class="rounded-xl">
          <v-toolbar color="teal darken-1" dark flat>
            <v-icon class="mr-2">mdi-format-list-checks</v-icon>
            ตรวจสอบเงื่อนไขอัตราว่าง
            <v-spacer></v-spacer>
            <v-btn icon @click="college_postionDialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-toolbar>

          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="subtitle-2 teal--text"
                    >เงื่อนไขสาขาวิชาที่เปิดรับ</v-card-title
                  >
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>คุณวุฒิ</th>
                        <th>สาขาวิชา</th>
                        <th class="text-center">รับ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, i) in conditionss" :key="i">
                        <td>{{ item.educational_level }}</td>
                        <td>{{ item.name_branch }}</td>
                        <td class="text-center font-weight-bold">
                          {{ item.quantity_n }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="subtitle-2 orange--text text--darken-2"
                    >ข้อมูลอัตราว่างปัจจุบัน</v-card-title
                  >
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th>สถานศึกษา</th>
                        <th class="text-center">ว่าง</th>
                        <th>กรณี</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, i) in man_powers" :key="'m' + i">
                        <td>{{ item.college_name }}</td>
                        <td class="text-center font-weight-bold error--text">
                          {{ item.num_position }}
                        </td>
                        <td>
                          <span class="caption">{{ item.case_vacancy }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- ─── คู่มือการใช้งานสำหรับเจ้าหน้าที่ใหม่ ─── -->
      <v-dialog v-model="guideDialog" max-width="700">
        <v-card rounded="lg">
          <div class="guide-header">
            <v-icon size="32" color="white" class="mr-3">mdi-book-open-page-variant-outline</v-icon>
            <div>
              <h3 class="white--text mb-0">วิธีใช้งานหน้าประมวลผลข้อมูลการย้าย</h3>
              <p class="white--text caption mb-0" style="opacity:.85">
                คำอธิบายกระบวนการทำงาน สำหรับเจ้าหน้าที่ที่เพิ่งเริ่มงาน
              </p>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="guideDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <v-card-text class="pt-5" style="max-height: 70vh; overflow-y: auto;">
            <div class="text-subtitle-1 font-weight-bold mb-2">หน้านี้ทำอะไร</div>
            <p>
              แสดงรายชื่อข้าราชการครูที่ยื่นขอย้าย จับคู่กับสถานศึกษาปลายทางที่ประกาศเปิดรับ
              พร้อมข้อมูลประกอบการพิจารณา (คะแนน, อัตรากำลังต้นทาง/ปลายทาง, ตำแหน่งว่าง) เพื่อให้
              คณะกรรมการ/เจ้าหน้าที่ตัดสินใจว่าจะประมวลผลย้ายผู้ใดลงตำแหน่งใด
            </p>

            <div class="text-subtitle-1 font-weight-bold mb-2 mt-4">
              ทำไมผู้ยื่นย้ายบางคนถึง "ไม่ถูกประมวลผล" หรือดูเหมือนหายไป
            </div>
            <v-alert type="warning" text dense border="left">
              คำถามที่พบบ่อยที่สุด: ตรวจสอบไม่พบผู้ยื่นย้ายในตาราง — สาเหตุหลักคือ
              <strong>ตำแหน่งว่างที่สถานศึกษาปลายทางประกาศรับย้ายถูกจองครบไปแล้ว</strong>
              (มีผู้อื่นได้รับการจัดสรรตำแหน่งนั้นไปก่อน) ผู้ยื่นย้ายรายนั้นยังคงมีคำร้องอยู่ในระบบ
              เพียงแต่ยังไม่สามารถลงตำแหน่งได้จนกว่าจะมีตำแหน่งว่างเพิ่ม — ระบบจะยังคงแสดงรายชื่อไว้
              ในตาราง พร้อมป้ายสีแดง "ไม่มีตำแหน่ง" ให้เห็นชัดเจน (ไม่ซ่อนอีกต่อไป) ให้สังเกตข้อความ
              อธิบายใต้ชื่อแต่ละคนในตาราง ซึ่งจะบอกเหตุผลไว้ตรงๆ เสมอ
            </v-alert>
            <p class="mt-2">เหตุการณ์อื่นที่ทำให้สับสนได้บ่อย:</p>
            <ul>
              <li>
                <strong>ยื่นย้ายไว้ แต่สถานศึกษาปลายทางยังไม่ประกาศรับ</strong> — ถ้าสถานศึกษายังไม่ได้
                ยื่นเงื่อนไขรับย้าย (conditions_transfer/conditions_branch) เข้ามาในรอบนี้ ผู้ยื่นย้าย
                จะไม่ปรากฏในหน้านี้เลย เพราะระบบจับคู่ได้เฉพาะปลายทางที่ประกาศรับไว้แล้วเท่านั้น
              </li>
              <li>
                <strong>ตำแหน่งว่างนับที่ระดับ "สถานศึกษา" ไม่ใช่ระดับ "สาขาวิชา"</strong> — ตัวเลข
                อัตราว่างที่แสดง เป็นจำนวนตำแหน่งว่างรวมของสถานศึกษานั้น ไม่ได้แยกเฉพาะสาขาที่ผู้ยื่น
                ย้ายสมัคร จึงอาจเห็นตำแหน่งว่าง "มี" แต่จริงๆ อาจเป็นอัตราของอีกสาขาหนึ่งก็ได้
              </li>
              <li>
                <strong>สำเร็จแล้ว (สีเขียว)</strong> — คำร้องนี้ถูกยืนยันตำแหน่งแล้ว ไม่จำเป็นต้อง
                ดำเนินการเพิ่ม
              </li>
            </ul>

            <div class="text-subtitle-1 font-weight-bold mb-2 mt-4">การ์ดสถิติด้านบน</div>
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr><td width="160" class="font-weight-medium">รายการทั้งหมด</td><td>จำนวนคำร้องขอย้ายที่จับคู่กับปลายทางได้ในรอบนี้ทั้งหมด</td></tr>
                <tr><td class="font-weight-medium">จับคู่สำเร็จ</td><td>จำนวนที่ยืนยันตำแหน่งเรียบร้อยแล้ว</td></tr>
                <tr><td class="font-weight-medium">อัตราว่างรับย้าย</td><td>ผลรวมตำแหน่งว่างจริง (ยังไม่ถูกจอง) ของทุกสถานศึกษาปลายทางในรายการ</td></tr>
                <tr><td class="font-weight-medium">รอดำเนินการ</td><td>ยังไม่ยืนยันตำแหน่ง — ถ้ามีตัวเลขสีแดงกำกับไว้ หมายถึงจำนวนที่ปลายทางไม่มีตำแหน่งว่างแล้ว</td></tr>
              </tbody>
            </v-simple-table>

            <div class="text-subtitle-1 font-weight-bold mb-2">ตัวกรองและปุ่มการทำงาน</div>
            <v-simple-table dense>
              <tbody>
                <tr><td width="200" class="font-weight-medium">แสดงทั้งหมด / กรอง 1:1</td><td>"1:1" = ไม่มีคู่แข่ง สถานศึกษาเปิดรับ มีตำแหน่งว่าง และมีผู้ยื่นย้ายเข้าเพียงคนเดียว เหมาะพิจารณาอนุมัติได้ทันที</td></tr>
                <tr><td class="font-weight-medium">แสดงเฉพาะที่มีตำแหน่งว่าง</td><td>ปิดโดย default (แสดงทุกรายการ) — เปิดสวิตช์นี้เพื่อซ่อนรายการที่ปลายทางไม่มีตำแหน่งว่างแล้วชั่วคราว</td></tr>
                <tr><td class="font-weight-medium">คลิกที่ป้าย "อัตราว่าง"</td><td>เปิดหน้าต่างดูรายละเอียดเงื่อนไขรับย้ายและตำแหน่งว่างของสถานศึกษานั้น</td></tr>
                <tr><td class="font-weight-medium">ค้นหาข้อมูล</td><td>ค้นหาได้จากทุกคอลัมน์ที่แสดงอยู่ในตาราง (ชื่อ, สถานศึกษา, สาขา ฯลฯ)</td></tr>
              </tbody>
            </v-simple-table>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" rounded @click="guideDialog = false">เข้าใจแล้ว</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ─── Position Select Dialog ─── -->
      <v-dialog v-model="positiondialog" persistent max-width="1000px">
        <v-card class="rounded-xl">
          <v-toolbar color="primary" dark flat>
            <v-icon class="mr-2">mdi-account-edit</v-icon>
            บันทึก/เลือกตำแหน่งย้ายไปปลายทาง
            <v-spacer></v-spacer>
            <v-btn icon @click="positiondialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-toolbar>

          <v-card-text class="pt-6">
            <v-form ref="updatepositionform" lazy-validation>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3 fill-height border-left-primary">
                    <div class="text-overline primary--text">
                      ข้อมูลปลายทาง (รับย้าย)
                    </div>
                    <div class="text-h6">
                      {{ transference_locations.college_name }}
                    </div>
                    <div class="subtitle-2 grey--text">
                      สาขา: {{ transference_locations.name_branch }}
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3 fill-height border-left-success">
                    <div class="text-overline success--text">
                      ข้อมูลผู้ขอย้าย
                    </div>
                    <div class="text-h6">
                      {{ transference_personnels.title_s
                      }}{{ transference_personnels.frist_name }}
                      {{ transference_personnels.last_name }}
                    </div>
                    <div class="subtitle-2 grey--text">
                      ปัจจุบัน: {{ transference_personnels.college_name }} ({{
                        transference_personnels.id_position
                      }})
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12">
                  <v-alert
                    border="left"
                    colored-border
                    color="info"
                    elevation="1"
                    icon="mdi-information"
                  >
                    <div class="subtitle-1 font-weight-bold">
                      เหตุผลการขอย้าย
                    </div>
                    <div v-if="transference_personnels.reason_1 === '1' || transference_personnels.reason_1 === 1">
                      - ย้ายเพื่ออยู่ร่วมคู่สมรส ({{
                        transference_personnels.r1_province
                      }})
                    </div>
                    <div v-if="transference_personnels.reason_2 === '1' || transference_personnels.reason_2 === 1">
                      - ย้ายเพื่อดูแลบิดา/มารดา ({{
                        transference_personnels.r2_province
                      }})
                    </div>
                    <div v-if="transference_personnels.reason_3 === '1' || transference_personnels.reason_3 === 1">
                      - ย้ายกลับภูมิลำเนา ({{
                        transference_personnels.r3_province
                      }})
                    </div>
                    <div v-if="transference_personnels.reason_4 === '1' || transference_personnels.reason_4 === 1">
                      - อื่นๆ: {{ transference_personnels.reason_4_detail }}
                    </div>
                  </v-alert>
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="updatepositions.id_position"
                    :items="man_powers"
                    item-text="college_position_case"
                    item-value="id_position"
                    label="เลือกเลขที่ตำแหน่งว่างเพื่อย้ายไปปลายทาง"
                    outlined
                    dense
                    prepend-inner-icon="mdi-chair-rolling"
                    :rules="[v => !!v || 'กรุณาเลือกตำแหน่ง']"
                    background-color="blue lighten-5"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title
                          >เลขที่ {{ item.id_position }} -
                          {{ item.position }}</v-list-item-title
                        >
                        <v-list-item-subtitle class="caption">{{
                          item.case_vacancy
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <!-- ข้อความเดิมเป็นภาษาอังกฤษของ Vuetify ("No data available") ไม่บอกสาเหตุ/
                         วิธีแก้ไข เจ้าหน้าที่จึงไม่รู้ว่าต้องไปเพิ่มอัตราว่างที่ไหน -->
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="text-wrap">
                            <v-icon color="error" left small>mdi-alert-circle-outline</v-icon>
                            ยังไม่มีเลขที่ตำแหน่งว่างในระบบสำหรับ
                            "{{ transference_locations.college_name }}"
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-wrap caption mt-1">
                            กรุณาไปเพิ่มอัตราว่างที่หน้า "บริหารอัตรารับย้าย" (admin/man_power)
                            ก่อน แล้วจึงย้อนกลับมาเลือกที่นี่อีกครั้ง
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="grey lighten-5 pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="positiondialog = false">ยกเลิก</v-btn>
            <v-btn color="success" elevation="2" @click="updatepositionSubmit">
              <v-icon left>mdi-content-save</v-icon> บันทึกข้อมูล
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>

    <!-- ─── Snackbar ─── -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{ snackbar.icon }}</v-icon> {{ snackbar.text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Movementmenu from "../../components/admin/movementmenu.vue";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { logSensitiveAccess } from "@/utils/sensitiveAccessLog";

export default {
  name: "HrvecProcessTransfer",
  components: { Movementmenu },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      search: "",
      activeTab: 0,
      onlyWithVacancy: false,
      guideDialog: false,

      conditions_transfers: [],
      periods: {},

      college_postionDialog: false,
      positiondialog: false,
      canceldialog: false,

      conditionss: [],
      man_powers: [],
      transference_locations: {},
      transference_personnels: {},
      transference_personnels_id_ref: {},

      updatepositions: {},
      updatepositions_condition: {},
      addreturn_man_power: {},

      conditons_transfer_successs: {},
      man_powerss: {},
      man_power_cancel: {},

      snackbar: { show: false, color: "", timeout: 4000, icon: "", text: "" },

      selectedHeaders: [],
      headersMap: [
        {
          text: "สถานะ",
          value: "college_code_susss",
          align: "center",
          width: "130px"
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "center",
          width: "100px",
          sortable: false
        },
        { text: "สถานศึกษา", value: "college_name", align: "left" },
        {
          text: "อัตราว่าง",
          value: "num_position",
          align: "center",
          width: "110px"
        },
        { text: "รับ", value: "quantity_n", align: "center", width: "60px" },
        {
          text: "ผู้ขอ",
          value: "personnel_num_s",
          align: "center",
          width: "70px"
        },
        {
          text: "ผู้ขอ(ทั้งหมด)",
          value: "personnel_num_all",
          align: "center",
          width: "100px"
        },
        { text: "สาขาวิชา", value: "name_branch", align: "left" },
        { text: "รอบ/ปี", value: "time_s", align: "center", width: "90px" },
        { text: "คะแนน", value: "point_s", align: "center", width: "80px" },
        {
          text: "คะแนนกรรมการ (สอจ.+ส่วนกลาง)",
          value: "committee_score",
          align: "center",
          width: "100px"
        },
        {
          text: "อัตรากำลัง (ปลายทาง)",
          value: "dest_rate_diff",
          align: "center",
          width: "130px"
        },
        {
          text: "อัตรากำลัง (ต้นทาง)",
          value: "origin_rate_diff",
          align: "center",
          width: "130px"
        },
        {
          text: "ความเห็น ผอ.",
          value: "comment_dr_c",
          align: "center",
          width: "160px"
        },
        { text: "ชื่อผู้ย้าย", value: "personnel_name", align: "left" },
        { text: "สถานศึกษาเดิม", value: "college_name_now", align: "left" },
        {
          text: "สรุปผล (คำอธิบาย)",
          value: "explain_result",
          align: "left",
          width: "320px",
          sortable: false
        }
      ]
    };
  },
  computed: {
    showHeaders() {
      return this.headersMap.filter(h => this.selectedHeaders.includes(h));
    },
    stats() {
      const total = this.conditions_transfers.length;
      const matched = this.conditions_transfers.filter(
        i => i.college_code_susss >= 1
      ).length;
      const vacancies = this.conditions_transfers.reduce(
        (acc, curr) => acc + (parseInt(curr.num_position) || 0),
        0
      );
      const pending = total - matched;
      // ในจำนวน "รอดำเนินการ" มีกี่รายที่ปลายทางไม่มีตำแหน่งว่างแล้ว (ตำแหน่งถูกจองครบ) — แยกออกมา
      // ให้ชัดเจน เพราะเป็นสาเหตุที่เจ้าหน้าที่ใหม่สับสนบ่อยที่สุดว่าทำไมประมวลผลไม่ได้
      const pendingNoVacancy = this.conditions_transfers.filter(
        i => !(i.college_code_susss >= 1) && (i.num_position === "0" || i.num_position == 0)
      ).length;
      return { total, matched, vacancies, pending, pendingNoVacancy };
    },
    // ─── ผู้สมัครที่มีคะแนนกรรมการ (committee_score) สูงสุดในแต่ละกลุ่มสถานศึกษาปลายทาง ───
    // อาศัยลำดับที่ backend ส่งมาแล้ว (ORDER BY ... cb.college_code ASC, committee_score DESC)
    // แถวแรกที่เจอของแต่ละ college_code ในอาเรย์ที่เรียงมาแล้ว คือผู้มีสิทธิ์สูงสุดของกลุ่มนั้น
    topCandidateKeys() {
      const seen = new Set();
      const top = new Set();
      for (const item of this.conditions_transfers) {
        const groupKey = item.college_code;
        if (groupKey && !seen.has(groupKey)) {
          seen.add(groupKey);
          if (item.id_tfp) top.add(item.id_tfp);
        }
      }
      return top;
    }
  },
  created() {
    const defaultCols = [
      "college_code_susss",
      "actions",
      "college_name",
      "num_position",
      "quantity_n",
      "personnel_num_s",
      "name_branch",
      "personnel_name",
      "point_s",
      "committee_score",
      "dest_rate_diff",
      "origin_rate_diff"
    ];
    this.selectedHeaders = this.headersMap.filter(h =>
      defaultCols.includes(h.value)
    );
  },
  async mounted() {
    await this.periodQuery();
    await this.conditions_transferQueryAll();
    logSensitiveAccess(
      this.$http,
      "process_transfer",
      "view",
      `รอบ ${this.periods.period_times}/${this.periods.period_year}`
    );
  },
  methods: {
    // ─── Row Color Classes ───
    row_classes(item) {
      // สำเร็จแล้ว — เขียวเด่น
      if (item.college_code_susss >= 1) return "row-success";
      // ไม่มีตำแหน่งว่าง — แดงจาง
      if (item.num_position === "0" || item.num_position == 0)
        return "row-no-position";
      // สับเปลี่ยน/แลกเปลี่ยน
      if (
        item.status_select === "sw_normal" ||
        item.status_select === "sw_agree"
      )
        return "row-swap";
      // ผู้มีคะแนนเกณฑ์เข้าเงื่อนไขสูงสุดในกลุ่มสถานศึกษาปลายทางเดียวกัน — เขียวอ่อน
      if (this.topCandidateKeys.has(item.id_tfp)) return "row-top-candidate";
      // รอพิจารณา — ส้มอ่อน
      return "row-pending";
    },

    getActionColor(item) {
      if (item.personnel_num_s <= 1 && item.num_position !== "0")
        return "#2E7D32";
      if (item.num_position === "0") return "#C62828";
      return "#E65100";
    },

    getApplicantChipColor(item) {
      const applicants = parseInt(item.personnel_num_s) || 0;
      const capacity = parseInt(item.quantity_n) || 0;
      if (applicants <= capacity) return "#C8E6C9";
      return "#FFCDD2";
    },

    getApplicantChipTextColor(item) {
      const applicants = parseInt(item.personnel_num_s) || 0;
      const capacity = parseInt(item.quantity_n) || 0;
      if (applicants <= capacity) return "#1B5E20";
      return "#B71C1C";
    },

    // point_s = จำนวนเหตุผลการย้ายที่ระบุ (reason_1..4, ค่า 0/1 ต่อข้อ รวมได้ 0-4)
    getScoreClass(score) {
      const s = parseInt(score) || 0;
      if (s >= 4) return "score-high";
      if (s >= 2) return "score-mid";
      return "score-low";
    },

    // committee_score = tpvecprovince_score (คะแนนกรรมการ สอจ.) + score_1420 (คะแนนส่วนกลาง)
    // รวมกันเป็นคะแนนเต็มประมาณ 0-100 — เกณฑ์สี high/mid/low นี้เป็นค่าอ้างอิงคร่าวๆ
    // ปรับตัวเลขได้ตามเกณฑ์การให้คะแนนจริงของ สอศ. หากมีประกาศเกณฑ์ที่ชัดเจนกว่านี้
    getCommitteeScoreClass(score) {
      const s = parseFloat(score) || 0;
      if (s >= 80) return "score-high";
      if (s >= 60) return "score-mid";
      return "score-low";
    },

    formatScore(score) {
      const s = parseFloat(score);
      if (isNaN(s)) return "-";
      return Number.isInteger(s) ? String(s) : s.toFixed(2);
    },

    rateDiffColor(diff) {
      const d = parseInt(diff) || 0;
      if (d < 0) return "red";
      if (d > 0) return "orange";
      return "success";
    },

    // สรุปผลด้วยภาษาธรรมชาติ — ประกอบประโยคจากฟิลด์ที่ backend คำนวณไว้แล้วเท่านั้น (point_s,
    // committee_score, personnel_num, num_position, num_position_total, num_position_booked,
    // dest_rate_diff, origin_rate_diff, condition_edu) ไม่เรียก AI/API ภายนอกใดๆ ทั้งสิ้น จึงไม่มี
    // ข้อมูลส่วนบุคคลรั่วไหลออกนอกระบบ (ไม่มีความเสี่ยง PDPA) และไม่แตะตรรกะการจัดลำดับ/คำนวณ
    // คะแนนเดิมเลย — เป็นแค่ชั้นแสดงผลเพิ่มเติมเท่านั้น
    explainResult(item) {
      const parts = [];

      const name = item.personnel_name || "ผู้ยื่นย้ายรายนี้";
      const dest = item.college_name || "สถานศึกษาปลายทาง";
      const branch = item.name_branch ? `สาขา${item.name_branch}` : "";
      parts.push(`${name} ยื่นขอย้ายไป ${dest}${branch ? " " + branch : ""}`);

      // จำนวนคู่แข่ง vs ตำแหน่งว่าง — buildGeneralListingQuery มี personnel_num_s,
      // buildOneToOneQuery มีแค่ personnel_num เฉยๆ ใช้ค่าไหนมีให้ก่อน
      const competitorCount =
        item.personnel_num_s !== undefined && item.personnel_num_s !== null
          ? parseInt(item.personnel_num_s)
          : parseInt(item.personnel_num);
      const posCount = parseInt(item.num_position) || 0;
      // กรณีตำแหน่งว่างที่ปลายทางถูกจองครบแล้ว (num_position = 0) — เดิมแถวนี้จะหายไปจากตาราง
      // ทั้งหมดเงียบๆ ทำให้เจ้าหน้าที่เข้าใจผิดว่าไม่มีคนยื่นย้าย ตอนนี้ยังคงแสดงไว้แต่ต้องอธิบาย
      // เหตุผลให้ชัดเจนที่สุด เพราะเป็นกรณีที่ทำให้เจ้าหน้าที่ใหม่สับสนบ่อยที่สุด
      if (posCount === 0) {
        const totalPos = parseInt(item.num_position_total) || 0;
        const bookedPos = parseInt(item.num_position_booked) || 0;
        if (totalPos > 0) {
          parts.push(
            `ตำแหน่งว่างที่ปลายทางมีทั้งหมด ${totalPos} อัตรา แต่ถูกจองไปแล้วครบทั้งหมด (${bookedPos} อัตรา) จึงยังประมวลผลย้ายลงตำแหน่งไม่ได้ในขณะนี้ ต้องรอตำแหน่งว่างเพิ่มหรือพิจารณาทางเลือกอื่น`
          );
        } else {
          parts.push("สถานศึกษาปลายทางยังไม่มีตำแหน่งว่างที่ประกาศรับย้ายเลย จึงยังประมวลผลย้ายลงตำแหน่งไม่ได้ในขณะนี้");
        }
      } else if (competitorCount === 1) {
        parts.push("ไม่มีคู่แข่ง (ผู้ยื่นย้ายเข้าตำแหน่งนี้เพียงคนเดียว)");
      } else if (competitorCount > 1) {
        parts.push(`มีคู่แข่ง ${competitorCount} คน สำหรับตำแหน่งว่าง ${posCount} อัตรา`);
      }

      if (item.committee_score !== undefined && item.committee_score !== null && item.committee_score !== "") {
        parts.push(`คะแนนกรรมการรวม ${this.formatScore(item.committee_score)} คะแนน`);
      }

      if (item.dest_rate_diff !== undefined && item.dest_rate_diff !== null) {
        const d = parseInt(item.dest_rate_diff) || 0;
        if (d < 0) {
          parts.push(`ปลายทางขาดอัตรากำลัง ${Math.abs(d)} อัตรา (ควรได้รับการพิจารณาก่อน)`);
        } else if (d > 0) {
          parts.push(`ปลายทางมีอัตรากำลังเกินเกณฑ์ ${d} อัตรา`);
        } else {
          parts.push("ปลายทางมีอัตรากำลังพอดีเกณฑ์");
        }
      }

      if (item.origin_rate_diff !== undefined && item.origin_rate_diff !== null) {
        const d = parseInt(item.origin_rate_diff) || 0;
        if (d > 0) {
          parts.push(`ต้นทางมีอัตรากำลังเกินเกณฑ์ ${d} อัตรา (ย้ายออกได้โดยไม่กระทบ)`);
        } else if (d < 0) {
          parts.push(`ต้นทางขาดอัตรากำลังอยู่แล้ว ${Math.abs(d)} อัตรา (ควรพิจารณาผลกระทบหากย้ายออก)`);
        }
      }

      if (item.condition_edu) {
        parts.push(`วุฒิที่สถานศึกษาต้องการ: ${item.condition_edu}`);
      }

      return parts.join(" · ");
    },

    showSnackbar(color, text) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.icon =
        color === "success" ? "mdi-check-circle" : "mdi-alert-circle";
      this.snackbar.show = true;
    },

    // ─── API Methods ─── (unchanged logic)
    async periodQuery() {
      try {
        let result = await this.$http.post("period.php", {
          ApiKey: this.ApiKey,
          period_enable_process: "1",
          period_id: "301"
        });
        this.periods = result.data;
      } catch (e) {
        console.error(e);
      }
    },

    async conditions_transferQueryAll() {
      this.loading = true;
      try {
        let result = await this.$http.post("process_transfer.php", {
          ApiKey: this.ApiKey,
          time_s: this.periods.period_times,
          year_s: this.periods.period_year,
          only_with_vacancy: this.onlyWithVacancy ? "1" : ""
        });
        this.conditions_transfers = result.data;
      } finally {
        this.loading = false;
      }
    },

    async OnetoOne() {
      this.loading = true;
      try {
        let result = await this.$http.post("process_transfer.php", {
          ApiKey: this.ApiKey,
          time_s: this.periods.period_times,
          year_s: this.periods.period_year,
          OnetoOne: "1"
        });
        this.conditions_transfers = result.data;
      } finally {
        this.loading = false;
      }
    },

    async collegePositon(college_code) {
      this.loading = true;
      try {
        let resCond = await this.$http.post("conditions_branch.php", {
          ApiKey: this.ApiKey,
          user_name: college_code
        });
        this.conditionss = resCond.data;

        let resMan = await this.$http.post("man_power.php", {
          ApiKey: this.ApiKey,
          college_code: college_code
        });
        this.man_powers = resMan.data;

        this.college_postionDialog = true;
      } finally {
        this.loading = false;
      }
    },

    async select_idPosition(id_tfl, id_tfp, college_code) {
      this.loading = true;
      try {
        let resLoc = await this.$http.post("transference_location.php", {
          ApiKey: this.ApiKey,
          id_tfl: id_tfl
        });
        this.transference_locations = resLoc.data;

        let resPer = await this.$http.post("transference_personnel.php", {
          ApiKey: this.ApiKey,
          id_tfp: id_tfp
        });
        this.transference_personnels = resPer.data;

        let resMan = await this.$http.post("man_power_process.php", {
          ApiKey: this.ApiKey,
          college_code: college_code
        });
        this.man_powers = resMan.data;

        this.positiondialog = true;
      } finally {
        this.loading = false;
      }
    },

    async updatepositionSubmit() {
      if (!this.$refs.updatepositionform.validate()) return;

      this.loading = true;
      try {
        this.updatepositions.ApiKey = this.ApiKey;
        this.updatepositions.ctstime_s = this.transference_personnels.time_ss;
        this.updatepositions.ctsyear_s = this.transference_personnels.year_ss;
        this.updatepositions.ctsid_postion_old = this.transference_personnels.id_position;
        this.updatepositions.ctscollege_code_old = this.transference_personnels.college_code;
        this.updatepositions.ctscollege_code = this.transference_locations.tlcollege_code;
        this.updatepositions.ctsid_branch = this.transference_locations.tlid_branch;
        this.updatepositions.ctsid_card = this.transference_personnels.id_card;
        this.updatepositions.ctsid_ref = this.transference_personnels.tid_ref;
        this.updatepositions.ctsid_position = this.updatepositions.id_position;
        this.updatepositions.ctsname_position = "ครู";
        this.updatepositions.ctsstatus_select = "agree";

        this.updatepositions_condition.ApiKey = this.ApiKey;
        this.updatepositions_condition.id_position = this.updatepositions.id_position;
        this.updatepositions_condition.status_booking = this.transference_personnels.id_card;

        this.addreturn_man_power.ApiKey = this.ApiKey;
        this.addreturn_man_power.college_code = this.transference_personnels.college_code;
        this.addreturn_man_power.id_position = this.transference_personnels.id_position;
        this.addreturn_man_power.position = "ครู";
        this.addreturn_man_power.case_vacancy = `ย้ายรอบ-${this.transference_personnels.time_ss}/${this.transference_personnels.year_ss}`;

        let resReturn = await this.$http.post(
          "man_power.insert.php",
          this.addreturn_man_power
        );
        if (resReturn.data.status) {
          let resUpdateMan = await this.$http.post(
            "man_power.update_process.php",
            this.updatepositions_condition
          );
          let resInsertSuccess = await this.$http.post(
            "conditons_transfer_success.insert.php",
            this.updatepositions
          );

          if (resUpdateMan.data.status && resInsertSuccess.data.status) {
            this.showSnackbar("success", "บันทึกข้อมูลสำเร็จ");
            this.positiondialog = false;
            this.conditions_transferQueryAll();
          } else {
            this.showSnackbar("error", "บันทึกสถานะปลายทางล้มเหลว");
          }
        } else {
          this.showSnackbar("error", "บันทึกตำแหน่งคืนต้นทางล้มเหลว");
        }
      } catch (e) {
        this.showSnackbar("error", "Server Error");
      } finally {
        this.loading = false;
      }
    },

    async deletePosition(id_ref) {
      this.loading = true;
      try {
        let resPer = await this.$http.post("transference_personnel.php", {
          ApiKey: this.ApiKey,
          id_ref: id_ref
        });
        this.transference_personnels_id_ref = resPer.data;

        let resMan = await this.$http.post("man_power.php", {
          ApiKey: this.ApiKey,
          id_card: this.transference_personnels_id_ref.id_card
        });
        this.man_powerss = resMan.data;

        let resCts = await this.$http.post("conditons_transfer_success.php", {
          ApiKey: this.ApiKey,
          ctsid_ref: id_ref
        });
        this.conditons_transfer_successs = resCts.data;

        this.canceldialog = true;
      } finally {
        this.loading = false;
      }
    },

    async cancelSubmit() {
      this.loading = true;
      try {
        this.man_powerss.ApiKey = this.ApiKey;
        this.man_powerss.status_booking = "";
        await this.$http.post("man_power.update_process.php", this.man_powerss);

        this.man_power_cancel.ApiKey = this.ApiKey;
        this.man_power_cancel.id_position = this.conditons_transfer_successs.ctsid_postion_old;
        await this.$http.post("man_power.delete.php", this.man_power_cancel);

        this.conditons_transfer_successs.ApiKey = this.ApiKey;
        let resDel = await this.$http.post(
          "conditons_transfer_success.delete.php",
          this.conditons_transfer_successs
        );

        if (resDel.data.status) {
          this.showSnackbar("success", "ยกเลิกรายการเรียบร้อย");
          this.canceldialog = false;
          this.conditions_transferQueryAll();
        } else {
          this.showSnackbar("error", "ลบข้อมูลล้มเหลว");
        }
      } finally {
        this.loading = false;
      }
    },

    exportToExcel() {
      const filename = `Process_Transfer_${new Date().toISOString().slice(0, 10)}.xlsx`;
      logSensitiveAccess(this.$http, "process_transfer", "export", filename);

      const exportData = this.conditions_transfers.map(item => ({
        สถานศึกษา: item.college_name,
        อัตราว่าง: item.num_position,
        จำนวนรับ: item.quantity_n,
        ผู้ขอ: item.personnel_num_s,
        ผู้ขอทั้งหมด: item.personnel_num_all,
        สาขาวิชา: item.name_branch,
        ชื่อผู้ย้าย: item.personnel_name,
        "จำนวนเหตุผลการย้าย (0-4)": item.point_s,
        "คะแนน สอจ.": item.tpvecprovince_score,
        "คะแนนส่วนกลาง": item.score_1420,
        "คะแนนกรรมการรวม": item.committee_score,
        "อัตรากำลังปลายทาง (สถานะ)": item.dest_rate_status,
        "อัตรากำลังปลายทาง (ผลต่าง)": item.dest_rate_diff,
        "อัตรากำลังต้นทาง (สถานะ)": item.origin_rate_status,
        "อัตรากำลังต้นทาง (ผลต่าง)": item.origin_rate_diff,
        ความเห็น: item.comment_dr_c,
        สถานะ: item.college_code_susss >= 1 ? "สำเร็จ" : "รอ"
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Process_Transfer");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });
      const data = new Blob([excelBuffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
      });
      saveAs(data, filename);
    }
  }
};
</script>

<style scoped>
/* ─── Legend ─── */
.legend-dot {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-right: 6px;
  flex-shrink: 0;
}

/* ─── Stat Cards ─── */
.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}

/* ─── Group Header (สถานศึกษาปลายทาง) ─── */
::v-deep .process-table .group-header-row {
  background-color: #eceff1 !important;
  border-top: 2px solid #90a4ae;
  border-bottom: 1px solid #cfd8dc;
  padding: 8px 12px !important;
}

/* ─── Row State Colors ─── */
::v-deep .process-table .row-success {
  background-color: #e8f5e9 !important;
  border-left: 4px solid #2e7d32;
  position: relative;
}
::v-deep .process-table .row-success:hover {
  background-color: #c8e6c9 !important;
}
::v-deep .process-table .row-success td:first-child {
  position: relative;
}
::v-deep .process-table .row-success td:first-child::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #2e7d32;
}

::v-deep .process-table .row-no-position {
  background-color: #ffebee !important;
  border-left: 4px solid #c62828;
  opacity: 0.75;
}
::v-deep .process-table .row-no-position:hover {
  background-color: #ffcdd2 !important;
  opacity: 1;
}
::v-deep .process-table .row-no-position td:first-child::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #c62828;
}

::v-deep .process-table .row-swap {
  background-color: #e3f2fd !important;
  border-left: 4px solid #1565c0;
}
::v-deep .process-table .row-swap:hover {
  background-color: #bbdefb !important;
}

::v-deep .process-table .row-pending {
  background-color: #fff8e1 !important;
  border-left: 4px solid #e65100;
}
::v-deep .process-table .row-pending:hover {
  background-color: #fff3e0 !important;
}

/* ผู้มีคะแนนเกณฑ์เข้าเงื่อนไขสูงสุดในกลุ่มสถานศึกษาปลายทางเดียวกัน — เขียวอ่อนกว่า row-success
   (row-success คือ "สำเร็จแล้วจริง" ส่วนอันนี้คือ "แนะนำ/มีสิทธิ์สูงสุด" ยังไม่ได้ยืนยัน) */
::v-deep .process-table .row-top-candidate {
  background-color: #f1f8e9 !important;
  border-left: 4px solid #7cb342;
}
::v-deep .process-table .row-top-candidate:hover {
  background-color: #dcedc8 !important;
}

/* ─── Table General ─── */
::v-deep .process-table .v-data-table__wrapper {
  overflow-x: auto;
}
::v-deep .process-table thead th {
  background-color: #263238 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
::v-deep .process-table thead th .v-icon {
  color: #ffffff !important;
}
::v-deep .process-table thead th .v-data-table-header__icon {
  color: #90a4ae !important;
}

::v-deep .process-table tbody td {
  font-size: 0.85rem !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  border-bottom: 1px solid #eceff1 !important;
}

/* ─── Score Colors ─── */
.score-high {
  color: #2e7d32;
  font-size: 1.05rem;
}
.score-mid {
  color: #e65100;
  font-size: 1.05rem;
}
.score-low {
  color: #78909c;
  font-size: 1.05rem;
}

/* ─── Status Chips pulse animation ─── */
.status-chip-success {
  animation: pulse-green 2s ease-in-out infinite;
}
@keyframes pulse-green {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(46, 125, 50, 0);
  }
}

.status-chip-no-position {
  animation: pulse-red 2.5s ease-in-out infinite;
}
@keyframes pulse-red {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(198, 40, 40, 0.25);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(198, 40, 40, 0);
  }
}

/* ─── Dialog helpers ─── */
.border-left-primary {
  border-left: 5px solid #1976d2;
}
.border-left-success {
  border-left: 5px solid #4caf50;
}

/* ─── Scrollbar ─── */
::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  height: 6px;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #b0bec5;
  border-radius: 3px;
}

/* ─── Guide Dialog ─── */
.guide-header {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
}
</style>
